import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Card, 
  Divider
} from 'antd';
import { 
  EditOutlined, 
  HistoryOutlined, 
  TagsOutlined, 
  SearchOutlined, 
  ThunderboltOutlined, 
  ArrowRightOutlined,
  CheckCircleOutlined,
  FolderOutlined
} from '@ant-design/icons';
import '../landing-page-enhanced.css';

const { Title, Paragraph } = Typography;

// 引导页面组件
function LandingPage() {
  const navigate = useNavigate();

  // 处理开始使用按钮点击
  const handleGetStarted = () => {
    navigate('/app');
  };

  // 滚动到指定区域
  const scrollToDemo = () => {
    const demoSection = document.querySelector('.app-screenshot');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动动画效果
  useEffect(() => {
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      
      elements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('animated');
          
          // 添加延迟动画
          const delay = element.getAttribute('data-delay');
          if (delay) {
            element.style.transitionDelay = delay;
          }
        }
      });
    };
    
    // 初始运行一次
    animateOnScroll();
    
    // 添加滚动事件监听
    window.addEventListener('scroll', animateOnScroll);
    
    // 清理函数
    return () => {
      window.removeEventListener('scroll', animateOnScroll);
    };
  }, []);

  return (
    <div className="landing-page min-h-screen bg-gradient-to-b from-white to-blue-50">
      {/* 导航栏 */}
      <header className="header sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 flex justify-between items-center py-4 w-full">
          <div className="flex items-center brand-logo">
            <svg t="1742008091301" className="icon h-10 mr-3" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2820" width="40" height="40">
              <path d="M514.29 257.53c137.98 1.23 249.48 113.46 249.48 251.75 0 44.76-11.69 86.81-32.16 123.22-41.8 74.32-120.23 125.29-210.8 128.39-1.01 0.04-2.01 0.06-3.02 0.07-0.66 0.01-1.32 0.02-1.96 0.04-1.27 0.02-2.55 0.04-3.82 0.04-53 0-96.35-43.35-96.35-96.35 0-24.63 9.36-47.18 24.7-64.25 1.17-1.29 2.36-2.56 3.59-3.79 17.47-17.47 41.56-28.31 68.06-28.31 85.82 0 155.41-69.57 155.41-155.41-0.01-85.07-68.37-154.17-153.13-155.4z" fill="#4770DC" p-id="2821"></path>
              <path d="M608.35 161.16c139.04 0 251.76 112.72 251.76 251.76 0 1.27-0.01 2.55-0.04 3.82-0.01 0.65-0.02 1.31-0.04 1.96-0.01 1.01-0.04 2.01-0.07 3.02-4.58 185.27-153.9 334.58-339.16 339.17 90.57-3.11 169-54.07 210.8-128.39 20.48-36.42 32.16-78.46 32.16-123.22 0-138.29-111.5-250.52-249.48-251.75-0.77-0.01-1.53-0.01-2.29-0.01-1.34 0-2.67 0.01-4.02 0.05-82.64 2.1-149.26 68.72-151.34 151.34 0.66-43.27 12.25-83.88 32.13-119.22 43.14-76.71 125.32-128.53 219.59-128.53z" fill="#6F94F4" p-id="2822"></path>
              <path d="M512 64.8c192.26 0 348.12 155.86 348.12 348.12 0-139.05-112.73-251.76-251.76-251.76-94.28 0-176.46 51.82-219.59 128.53-19.88 35.33-31.47 75.95-32.13 119.22-0.04 1.34-0.05 2.67-0.05 4.02v449.92c0 53-43.37 96.35-96.35 96.35-53 0-96.35-43.35-96.35-96.35V412.92c0-136.41 78.45-254.49 192.71-311.59C403.36 77.96 456.15 64.8 512 64.8z" fill="#97B4FF" p-id="2823"></path>
            </svg>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">PromptPro</span>
          </div>
          <div className="flex items-center space-x-4">
            <a 
              href="https://www.xiaohongshu.com/user/profile/6350c34d0000000018028b8c?xsec_token=YB2Mfg3kbL3Dnx0iqPWEyzoCrpwSzemqdly0c1pRHXCms=&xsec_source=app_share&xhsshare=CopyLink&appuid=6350c34d0000000018028b8c&apptime=1741968183&share_id=138849424ecf44c1a2af6f7083b529be"
              target="_blank"
              rel="noopener noreferrer" 
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              小红书
            </a>
            <Button 
              type="primary" 
              icon={<ArrowRightOutlined />} 
              className="bg-gradient-to-r from-blue-500 to-blue-400 border-none shadow-md hover:shadow-lg transition-all"
              onClick={handleGetStarted}
            >
              立即体验
            </Button>
          </div>
        </div>
      </header>

      {/* 主要内容 */}
      <main>
        {/* 英雄区域 */}
        <section className="hero py-16 md:py-24 relative overflow-hidden">
          {/* 背景装饰 */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="blob blob-1"></div>
            <div className="blob blob-2"></div>
          </div>
          
          <div className="container mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-4xl mx-auto animate-sequence">
              {/* 徽章区域 */}
              <div className="flex justify-center mb-6">
                <span className="hero-badge inline-flex items-center gap-2">
                  <span>AI提示词管理的最佳解决方案</span>
                  <a href="#features" className="badge-link text-xs" onClick={(e) => { e.preventDefault(); scrollToDemo(); }}>
                    了解更多 <ArrowRightOutlined style={{ fontSize: '10px' }} />
                  </a>
                </span>
              </div>
              
              {/* 标题区域 */}
              <div className="text-center animate-on-scroll" data-animation="fadeInUp">
                <h1 className="hero-title">
                  <span className="hero-title-colored">产品化管理</span> <span className="hero-title-highlight">你的提示词</span>
                </h1>
                <div className="hero-subtitle mb-8">
                  每次迭代都是价值升级，构建您最有价值的数字产品
                </div>
              </div>
              
              {/* 操作按钮区域 */}
              <div className="flex flex-wrap justify-center gap-6 mb-10">
                <Button 
                  type="primary"
                  size="large"
                  onClick={handleGetStarted}
                  className="hero-button px-10 py-3 h-auto text-lg font-medium"
                >
                  免费开始使用
                </Button>
                <Button 
                  size="large"
                  className="secondary-button px-8 py-3 h-auto text-base font-medium"
                  onClick={scrollToDemo}
                >
                  查看演示
                </Button>
              </div>
              
              {/* 特性标签 */}
              <div className="flex flex-wrap justify-center gap-5 text-sm text-gray-500">
                <div className="flex items-center">
                  <CheckCircleOutlined className="text-blue-500 mr-2" />
                  <span>简单易用</span>
                </div>
                <Divider type="vertical" className="bg-gray-300 h-4" />
                <div className="flex items-center">
                  <CheckCircleOutlined className="text-blue-500 mr-2" />
                  <span>本地存储</span>
                </div>
                <Divider type="vertical" className="bg-gray-300 h-4" />
                <div className="flex items-center">
                  <CheckCircleOutlined className="text-blue-500 mr-2" />
                  <span>版本迭代</span>
                </div>
                <Divider type="vertical" className="bg-gray-300 h-4" />
                <div className="flex items-center">
                  <CheckCircleOutlined className="text-blue-500 mr-2" />
                  <span>文件夹管理</span>
                </div>
              </div>
            </div>
            
            {/* 滚动指示器已移除 */}
            
          </div>
        </section>

        {/* 应用截图展示区域 */}
        <section className="app-screenshot py-16 pt-8 bg-gradient-to-b from-white to-gray-50 relative">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-10 animate-on-scroll" data-animation="fadeInUp">
              <span className="inline-block px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-4 shadow-sm">
                产品演示
              </span>
              <Title level={2} className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                直观高效的用户界面
              </Title>
              <Paragraph className="text-gray-600 max-w-2xl mx-auto text-lg">
                简洁优雅的设计，让您的提示词管理体验更加流畅，就像使用专业的代码编辑器一样
              </Paragraph>
            </div>
            
            <div className="relative mx-auto max-w-5xl animate-on-scroll" data-animation="fadeInUp">
              {/* 装饰元素 */}
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-100 rounded-full opacity-30 blur-3xl"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-indigo-100 rounded-full opacity-30 blur-3xl"></div>
              
              {/* 截图容器 */}
              <div className="screenshot-container relative z-10">
                <img 
                  src="/images/yanshi3.png" 
                  alt="PromptPro应用截图" 
                  className="w-full h-auto"
                />
                
                {/* 移除标签管理系统浮动提示框 */}
                
              </div>
              
              {/* 特性指示器 */}
              <div className="flex justify-center mt-8 gap-4">
                <div className="w-3 h-3 rounded-full bg-blue-500 opacity-70"></div>
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
              </div>
            </div>
          </div>
          
          {/* 波浪分隔线 */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto">
              <path fill="#f9fafb" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,149.3C960,160,1056,160,1152,138.7C1248,117,1344,75,1392,53.3L1440,32L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </section>

        {/* 功能区域 */}
        <section className="features py-16 bg-gradient-to-b from-gray-50 to-gray-100">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-12 animate-on-scroll" data-animation="fadeInUp">
              <span className="inline-block px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-4 shadow-sm">
                核心功能
              </span>
              <Title level={2} className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                精心设计的功能体验
              </Title>
              <Paragraph className="text-gray-600 max-w-2xl mx-auto text-lg">
                为AI从业者和爱好者打造的专业提示词版本控制与迭代系统，让您像产品经理一样优化每一个提示词
              </Paragraph>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* 功能卡片1 */}
              <Card 
                className="feature-card-enhanced animate-on-scroll"
                data-animation="fadeInUp"
                data-delay="0.1s"
                styles={{ 
                  body: { padding: '0' }
                }}
              >
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-blue-500 to-blue-400 text-white">
                    <EditOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={3} className="text-xl font-semibold mb-3 text-center">
                    提示词创建与编辑
                  </Title>
                  <Paragraph className="text-gray-600 text-center">
                    像专业编辑器一样创建和修改提示词，支持实时预览和格式化，让您的提示词更加精准有效
                  </Paragraph>
                </div>
                <div className="bg-gray-50 p-4 border-t">
                  <div className="flex items-center text-blue-500 justify-center">
                    <span className="text-sm font-medium">了解更多</span>
                    <ArrowRightOutlined className="ml-1" />
                  </div>
                </div>
              </Card>

              {/* 功能卡片2 */}
              <Card 
                className="feature-card-enhanced animate-on-scroll"
                data-animation="fadeInUp"
                data-delay="0.2s"
                styles={{ 
                  body: { padding: '0' }
                }}
              >
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-green-500 to-green-400 text-white">
                    <HistoryOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={3} className="text-xl font-semibold mb-3 text-center">
                    版本迭代管理
                  </Title>
                  <Paragraph className="text-gray-600 text-center">
                    像产品经理一样迭代您的提示词，记录每次改进，对比不同版本效果，见证AI输出质量的持续提升
                  </Paragraph>
                </div>
                <div className="bg-gray-50 p-4 border-t">
                  <div className="flex items-center text-green-500 justify-center">
                    <span className="text-sm font-medium">了解更多</span>
                    <ArrowRightOutlined className="ml-1" />
                  </div>
                </div>
              </Card>

              {/* 功能卡片3 */}
              <Card 
                className="feature-card-enhanced animate-on-scroll"
                data-animation="fadeInUp"
                data-delay="0.3s"
                styles={{ 
                  body: { padding: '0' }
                }}
              >
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-purple-500 to-purple-400 text-white">
                    <TagsOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={3} className="text-xl font-semibold mb-3 text-center">
                    文件夹与标签系统
                  </Title>
                  <Paragraph className="text-gray-600 text-center">
                    像管理代码仓库一样组织您的提示词，通过版本迭代不断优化代码生成效果
                  </Paragraph>
                </div>
                <div className="bg-gray-50 p-4 border-t">
                  <div className="flex items-center text-purple-500 justify-center">
                    <span className="text-sm font-medium">了解更多</span>
                    <ArrowRightOutlined className="ml-1" />
                  </div>
                </div>
              </Card>
            </div>

            {/* 第二行功能卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              {/* 功能卡片4 */}
              <Card 
                className="feature-card-enhanced animate-on-scroll"
                data-animation="fadeInUp"
                data-delay="0.1s"
                styles={{ 
                  body: { padding: '0' }
                }}
              >
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-yellow-500 to-yellow-400 text-white">
                    <SearchOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={3} className="text-xl font-semibold mb-3 text-center">
                    高级搜索功能
                  </Title>
                  <Paragraph className="text-gray-600 text-center">
                    多维度智能搜索系统，快速定位所需提示词，支持按标题、内容、标签和文件夹筛选，提高工作效率
                  </Paragraph>
                </div>
                <div className="bg-gray-50 p-4 border-t">
                  <div className="flex items-center text-yellow-500 justify-center">
                    <span className="text-sm font-medium">了解更多</span>
                    <ArrowRightOutlined className="ml-1" />
                  </div>
                </div>
              </Card>

              {/* 功能卡片5 */}
              <Card 
                className="feature-card-enhanced animate-on-scroll"
                data-animation="fadeInUp"
                data-delay="0.2s"
                styles={{ 
                  body: { padding: '0' }
                }}
              >
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-red-500 to-red-400 text-white">
                    <ThunderboltOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={3} className="text-xl font-semibold mb-3 text-center">
                    美观的用户界面
                  </Title>
                  <Paragraph className="text-gray-600 text-center">
                    精心设计的视觉体验，包括平滑动画、立体阴影和丰富的色彩系统，让提示词管理不再枯燥
                  </Paragraph>
                </div>
                <div className="bg-gray-50 p-4 border-t">
                  <div className="flex items-center text-red-500 justify-center">
                    <span className="text-sm font-medium">了解更多</span>
                    <ArrowRightOutlined className="ml-1" />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* 使用场景 */}
        <section className="use-cases py-16 bg-gradient-to-b from-gray-100 to-white relative">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-12 animate-on-scroll" data-animation="fadeInUp">
              <span className="inline-block px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-4 shadow-sm">
                适用场景
              </span>
              <Title level={2} className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                为各种场景量身定制
              </Title>
              <Paragraph className="text-gray-600 max-w-2xl mx-auto text-lg">
                无论您是AI爱好者、内容创作者还是开发人员，PromptPro都能帮助您像专业人士一样管理和优化提示词
              </Paragraph>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="feature-card-enhanced animate-on-scroll" data-animation="fadeInUp" data-delay="0.1s">
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-blue-500 to-blue-400 text-white">
                    <EditOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={4} className="text-xl font-semibold mb-3 text-center">内容创作</Title>
                  <Paragraph className="text-gray-600 text-center">
                    像产品经理一样迭代您的创作提示词，记录每次改进，持续提升AI生成内容的质量
                  </Paragraph>
                  <ul className="mt-4 space-y-3">
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-blue-500 mt-1 mr-2" />
                      <span className="text-gray-600">博客和文章写作</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-blue-500 mt-1 mr-2" />
                      <span className="text-gray-600">社交媒体内容</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-blue-500 mt-1 mr-2" />
                      <span className="text-gray-600">营销文案创作</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="feature-card-enhanced animate-on-scroll" data-animation="fadeInUp" data-delay="0.2s">
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-green-500 to-green-400 text-white">
                    <FolderOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={4} className="text-xl font-semibold mb-3 text-center">编程开发</Title>
                  <Paragraph className="text-gray-600 text-center">
                    像管理代码仓库一样组织您的编程提示词，通过版本迭代不断优化代码生成效果
                  </Paragraph>
                  <ul className="mt-4 space-y-3">
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-2" />
                      <span className="text-gray-600">代码生成与重构</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-2" />
                      <span className="text-gray-600">调试与问题解决</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-green-500 mt-1 mr-2" />
                      <span className="text-gray-600">技术文档编写</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="feature-card-enhanced animate-on-scroll" data-animation="fadeInUp" data-delay="0.3s">
                <div className="p-8">
                  <div className="feature-icon-enhanced bg-gradient-to-br from-purple-500 to-purple-400 text-white">
                    <SearchOutlined style={{ fontSize: '28px' }} />
                  </div>
                  <Title level={4} className="text-xl font-semibold mb-3 text-center">研究与学习</Title>
                  <Paragraph className="text-gray-600 text-center">
                    构建个人知识提示词库，通过文件夹分类和标签管理，让AI辅助学习更加高效
                  </Paragraph>
                  <ul className="mt-4 space-y-3">
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-purple-500 mt-1 mr-2" />
                      <span className="text-gray-600">学术研究辅助</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-purple-500 mt-1 mr-2" />
                      <span className="text-gray-600">知识整理与归纳</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleOutlined className="text-purple-500 mt-1 mr-2" />
                      <span className="text-gray-600">学习资料生成</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* 装饰元素 */}
          <div className="absolute -bottom-10 right-0 w-1/3 h-40 bg-gradient-to-r from-transparent to-blue-50 opacity-70 transform skew-y-3 hidden md:block"></div>
        </section>

        {/* 行动召唤 */}
        <section className="cta-section py-20">
          <div className="container mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-3xl mx-auto animate-on-scroll" data-animation="fadeInUp">
              <div className="text-center">
                <span className="cta-badge inline-block mb-6">开始使用</span>
                <h2 className="cta-title mb-8">告别混乱，迎接提示词产品化管理</h2>
                <Button 
                  size="large"
                  onClick={handleGetStarted}
                  className="cta-button px-10 py-3 h-auto text-lg font-medium"
                >
                  免费开始使用
                </Button>
              </div>
              
              {/* 装饰元素 */}
              <div className="cta-decoration mt-12">
                <div className="cta-decoration-line"></div>
              </div>
            </div>
          </div>
        </section>

        {/* 页脚 */}
        <footer className="footer-section py-12 relative overflow-hidden">
          <div className="container mx-auto px-6 md:px-12 relative z-10">
            {/* 删除背景装饰元素 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {/* 品牌信息 */}
              <div className="flex flex-col items-center md:items-start">
                <div className="flex items-center mb-4">
                  <svg t="1742008091301" className="icon h-8 mr-2" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2820" width="32" height="32">
                    <path d="M514.29 257.53c137.98 1.23 249.48 113.46 249.48 251.75 0 44.76-11.69 86.81-32.16 123.22-41.8 74.32-120.23 125.29-210.8 128.39-1.01 0.04-2.01 0.06-3.02 0.07-0.66 0.01-1.32 0.02-1.96 0.04-1.27 0.02-2.55 0.04-3.82 0.04-53 0-96.35-43.35-96.35-96.35 0-24.63 9.36-47.18 24.7-64.25 1.17-1.29 2.36-2.56 3.59-3.79 17.47-17.47 41.56-28.31 68.06-28.31 85.82 0 155.41-69.57 155.41-155.41-0.01-85.07-68.37-154.17-153.13-155.4z" fill="#4770DC" p-id="2821"></path>
                    <path d="M608.35 161.16c139.04 0 251.76 112.72 251.76 251.76 0 1.27-0.01 2.55-0.04 3.82-0.01 0.65-0.02 1.31-0.04 1.96-0.01 1.01-0.04 2.01-0.07 3.02-4.58 185.27-153.9 334.58-339.16 339.17 90.57-3.11 169-54.07 210.8-128.39 20.48-36.42 32.16-78.46 32.16-123.22 0-138.29-111.5-250.52-249.48-251.75-0.77-0.01-1.53-0.01-2.29-0.01-1.34 0-2.67 0.01-4.02 0.05-82.64 2.1-149.26 68.72-151.34 151.34 0.66-43.27 12.25-83.88 32.13-119.22 43.14-76.71 125.32-128.53 219.59-128.53z" fill="#6F94F4" p-id="2822"></path>
                    <path d="M512 64.8c192.26 0 348.12 155.86 348.12 348.12 0-139.05-112.73-251.76-251.76-251.76-94.28 0-176.46 51.82-219.59 128.53-19.88 35.33-31.47 75.95-32.13 119.22-0.04 1.34-0.05 2.67-0.05 4.02v449.92c0 53-43.37 96.35-96.35 96.35-53 0-96.35-43.35-96.35-96.35V412.92c0-136.41 78.45-254.49 192.71-311.59C403.36 77.96 456.15 64.8 512 64.8z" fill="#97B4FF" p-id="2823"></path>
                  </svg>
                  <span className="footer-brand-text text-xl font-bold">PromptPro</span>
                </div>
                <p className="text-gray-500 mb-4 text-center md:text-left">提示词产品化管理的最佳解决方案</p>
              </div>
              
              {/* 快速链接 */}
              <div className="flex flex-col items-center md:items-start">
                <h3 className="footer-heading mb-4">快速链接</h3>
                <ul className="space-y-2">
                  <li><a href="#hero" className="footer-link">首页</a></li>
                  <li><a href="#features" className="footer-link">功能介绍</a></li>
                  <li><a href="#tutorial" className="footer-link">使用教程</a></li>
                  <li><a href="#faq" className="footer-link">常见问题</a></li>
                </ul>
              </div>
              
              {/* 联系我们 */}
              <div className="flex flex-col items-center md:items-start">
                <h3 className="footer-heading mb-4">联系我们</h3>
                <div className="space-y-3">
                  <a 
                    href="https://www.xiaohongshu.com/user/profile/6350c34d0000000018028b8c?xsec_token=YB2Mfg3kbL3Dnx0iqPWEyzoCrpwSzemqdly0c1pRHXCms=&xsec_source=app_share&xhsshare=CopyLink&appuid=6350c34d0000000018028b8c&apptime=1741968183&share_id=138849424ecf44c1a2af6f7083b529be" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="footer-contact-item"
                  >
                    <div className="footer-contact-icon bg-red-50 text-red-500">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.5 12H15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 8.5V15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span>小红书</span>
                  </a>
                  <div className="footer-contact-item">
                    <div className="footer-contact-icon bg-green-50 text-green-500">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 2H7C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2Z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 11.37C16.1234 12.2022 15.9813 13.0522 15.5938 13.799C15.2063 14.5458 14.5931 15.1514 13.8416 15.5297C13.0901 15.9079 12.2384 16.0396 11.4078 15.9059C10.5771 15.7723 9.80976 15.3801 9.21484 14.7852C8.61992 14.1902 8.22773 13.4229 8.09407 12.5922C7.9604 11.7615 8.09207 10.9099 8.47033 10.1584C8.84859 9.40685 9.45419 8.79374 10.201 8.40624C10.9478 8.01874 11.7978 7.87659 12.63 8C13.4789 8.12588 14.2649 8.52146 14.8717 9.12831C15.4785 9.73515 15.8741 10.5211 16 11.37Z" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <span>微信：</span>
                      <span className="font-medium">Joy10241128</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 分隔线 */}
            <div className="footer-divider my-6"></div>
            
            {/* 版权信息 */}
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500 text-sm mb-2 md:mb-0">
                {new Date().getFullYear()} 摸鱼小李
              </p>
              <div className="flex space-x-4">
                <a href="/privacy" className="text-gray-500 text-sm hover:text-blue-500 transition-colors">隐私政策</a>
                <a href="/terms" className="text-gray-500 text-sm hover:text-blue-500 transition-colors">服务条款</a>
              </div>
            </div>
          </div>
        </footer>
        
        {/* 自定义样式 */}
        <style>{`
          body, html {
            height: auto !important;
            overflow-y: auto !important;
            overflow-x: hidden;
          }
          
          .landing-page {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            overflow-y: auto;
            height: auto;
            min-height: 100vh;
          }
          
          .feature-card {
            height: 100%;
            transition: all 0.3s ease;
          }
          
          .feature-card:hover {
            transform: translateY(-5px);
          }
          
          .feature-icon {
            transition: all 0.3s ease;
          }
          
          .feature-card:hover .feature-icon {
            transform: scale(1.1);
          }
        `}</style>
      </main>
    </div>
  );
}

export default LandingPage;
